<script setup lang="ts">
import {computed, PropType, ref} from 'vue';
import { useI18n } from 'vue-i18n';
import CustomModal from '@/components/CustomModal/CustomModal.vue';
import CustomButton from '@/components/Button/Button.vue';
import CustomSpinner from "@/components/CustomSpinner/CustomSpinner.vue";
import {VueSpinner} from "vue3-spinners";

const props = defineProps({
  show: {
    type: Boolean,
    required: true
  },
  yacht: {
    type: Object as PropType<{ id?: number | string; name?: string } | null>,
    default: null
  },
  isDeleting: {
    type: Boolean,
    default: false
  }
})


const emit = defineEmits<{
  (e: 'update:show', value: boolean): void;
  (e: 'on-close'): void;
  (e: 'on-delete-click', yachtId: number | string | null): void;
}>();

const { t, te } = useI18n();

const yachtId = computed(() => props.yacht?.id ?? null);
const yachtName = computed(() => props.yacht?.name || t('yachtDelete.unknownYacht'));

const titleText = computed(() => te('yachtDelete.title') ? t('yachtDelete.title') : 'Delete saved offer?');
const messageText = computed(() =>
    te('yachtDelete.message')
        ? t('yachtDelete.message', { name: yachtName.value })
        : `This will remove "${yachtName.value}" from your saved offers. This action cannot be undone.`
);

function handleClose() {
  emit('update:show', false);
  emit('on-close');
}

async function handleConfirm() {
    emit('on-delete-click', yachtId.value);
}
</script>


<template>
  <CustomModal
      :show="show"
      @close="handleClose"
      classes="w-1/6"
      :mobile-fullscreen="false"
      :custom-index="9999"
      extra-classes="h-fit"
      drawerHeight="fit-content"
  >
    <div class="p-2 flex flex-col sm:max-w-sm sm:w-full container">
      <div class="cursor-pointer self-end hidden sm:block" type="default" @click="handleClose">
        <img src="@/assets/icons/svg/Close_16x16.svg" alt="Close" width="18"/>
      </div>
      <div class="p-3.5 flex flex-col gap-8">
        <div class="responsive-title-6 font-bold">{{ titleText }}</div>
        <p>{{ messageText }}</p>
        <div class="flex gap-4 justify-space-between">
          <CustomButton
              type="black"
              class="deactivate-btn w-1/3 hover:opacity-30 hover:text-white hover:border-red font-semibold responsive-body-medium"
              :outlined="false"
              :rounded="true"
              :disabled="isDeleting"
              @click="handleConfirm"
          >
            <span class="flex items-center gap-2">
              <VueSpinner v-if="isDeleting" :size="18" color="white"/>
              <span>{{ t('yachtDelete.delete') }}</span>
            </span>
          </CustomButton>
          <CustomButton
              :outlined="true"
              class="self-start hover:bg-black hover:text-white hover:border-black transition-all"
              :rounded="true"
              @click="handleClose"
          >
            {{ t('yachtDelete.cancel') }}
          </CustomButton>
        </div>
      </div>
    </div>
  </CustomModal>
</template>

<style scoped>
.deactivate-btn {
  background-color: #ff0000 !important;
}

</style>
